<div class="fc-root" [class.fc-playback]="isPlayback" (click)="$event.stopPropagation()">
  <button type="button" class="fc-close-float" (click)="close()" title="Close" aria-label="Close">
    <i class="bi bi-x-lg"></i>
  </button>

  <div class="fc-main">
    <div
      class="fc-canvas-wrap"
      [class.fc-side-wind]="side === 'wind'"
      [class.fc-side-calculator]="side === 'calculator'"
      [class.fc-cursor-draw]="!isPlayback && (tool === 'dot' || tool === 'line')"
      [class.fc-cursor-grab]="!isPlayback && tool === 'navigate'"
      [class.fc-readonly]="isPlayback"
      (pointerdown)="onPointerDown($event)"
      (pointermove)="onPointerMove($event)"
      (pointerup)="onPointerUp($event)"
      (pointercancel)="onPointerUp($event)"
    >
      @if (loading) {
        <div class="fc-status">Loading E6B…</div>
      }
      @if (loadError) {
        <div class="fc-status fc-status-error">{{ loadError }}</div>
      }
      <div #svgHost class="fc-svg-host"></div>
      @if (isPlayback && captionText) {
        <div class="fc-caption" aria-live="polite">{{ captionText }}</div>
      }
    </div>

    @if (isPlayback) {
      <aside class="fc-toolbar fc-toolbar-playback" aria-label="E6B playback">
        <button type="button" class="fc-tool" (click)="playbackPrev()" title="Previous step" aria-label="Previous step">
          <i class="bi bi-skip-start-fill"></i>
        </button>
        <button
          type="button"
          class="fc-tool"
          (click)="playbackTogglePlay()"
          [title]="playbackPlaying ? 'Pause' : 'Play'"
          [attr.aria-label]="playbackPlaying ? 'Pause' : 'Play'"
        >
          <i class="bi" [class.bi-pause-fill]="playbackPlaying" [class.bi-play-fill]="!playbackPlaying"></i>
        </button>
        <button type="button" class="fc-tool" (click)="playbackNext()" title="Next step" aria-label="Next step">
          <i class="bi bi-skip-end-fill"></i>
        </button>
        <button type="button" class="fc-tool" (click)="playbackReplay()" title="Replay" aria-label="Replay">
          <i class="bi bi-arrow-counterclockwise"></i>
        </button>
        <button type="button" class="fc-tool fc-close" (click)="close()" title="Close" aria-label="Close">
          <i class="bi bi-x-lg"></i>
        </button>
        <div class="fc-playback-meta" aria-live="polite">{{ playbackMeta }}</div>
      </aside>
    } @else {
      <aside class="fc-toolbar" aria-label="E6B tools">
        <button type="button" class="fc-tool fc-close" (click)="close()" title="Close" aria-label="Close">
          <i class="bi bi-x-lg"></i>
        </button>

        <div class="fc-scale-group">
          <button type="button" class="fc-tool" [class.fc-tool-active]="scale === 'low'" (click)="setScale('low')" [disabled]="side !== 'wind'" title="Low speed scale">Low</button>
          <button type="button" class="fc-tool" [class.fc-tool-active]="scale === 'high'" (click)="setScale('high')" [disabled]="side !== 'wind'" title="High speed scale">High</button>
        </div>

        <button
          type="button"
          class="fc-tool fc-scale-switch"
          (click)="toggleScale()"
          [disabled]="side !== 'wind' || !highScaleAvailable"
          [attr.aria-pressed]="scale === 'high'"
          title="Toggle low / high speed scale"
          aria-label="Toggle low or high speed scale"
        >
          <span class="fc-scale-switch-opt" [class.fc-scale-switch-on]="scale === 'low'">Low</span>
          <span class="fc-scale-switch-opt" [class.fc-scale-switch-on]="scale === 'high'">High</span>
        </button>

        <button type="button" class="fc-tool" (click)="toggleSide()" title="Switch E6B side" aria-label="Switch side">
          <i class="bi bi-arrow-repeat"></i>
        </button>
        <button type="button" class="fc-tool" [class.fc-tool-active]="tool === 'dot'" (click)="setTool('dot')" [disabled]="side !== 'wind'" title="Place the mark on the wheel (M); marking again moves it" aria-label="Place mark">
          <svg class="fc-mark-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true">
            <circle cx="8" cy="8" r="3.4" />
            <path d="M3.62 3.62 L6.23 6.23 M9.77 9.77 L12.38 12.38 M3.62 12.38 L6.23 9.77 M9.77 6.23 L12.38 3.62" />
          </svg>
        </button>
        <button type="button" class="fc-tool" [class.fc-tool-active]="tool === 'line'" (click)="setTool('line')" [disabled]="side !== 'wind'" title="Draw a line on the wheel (L); the last three stay" aria-label="Draw line">
          <i class="bi bi-pencil"></i>
        </button>
        <button type="button" class="fc-tool" (click)="undo()" [disabled]="side !== 'wind' || !canUndo" title="Undo last mark" aria-label="Undo">
          <i class="bi bi-arrow-counterclockwise"></i>
        </button>

        <div class="fc-tool-group fc-tool-group-secondary">
          <button type="button" class="fc-tool" (click)="zoomIn()" title="Zoom in" aria-label="Zoom in">
            <i class="bi bi-zoom-in"></i>
          </button>
          <button type="button" class="fc-tool" (click)="zoomOut()" title="Zoom out" aria-label="Zoom out">
            <i class="bi bi-zoom-out"></i>
          </button>
          @if (keyboardAvailable) {
            <button
              type="button"
              class="fc-tool"
              [class.fc-tool-active]="showShortcuts"
              (click)="toggleShortcuts()"
              title="Keyboard shortcuts"
              aria-label="Keyboard shortcuts"
              [attr.aria-expanded]="showShortcuts"
            >
              <i class="bi bi-keyboard"></i>
            </button>
          }
        </div>

        <button type="button" class="fc-tool fc-reset" (click)="reset()" title="Reset side" aria-label="Reset">Reset</button>
      </aside>
    }
  </div>

  @if (!isPlayback && showShortcuts && keyboardAvailable) {
    <div class="fc-shortcuts" role="note" aria-label="E6B shortcuts">
      <div class="fc-shortcuts-title">Shortcuts</div>
      <ul>
        <li><kbd>+</kbd> / <kbd>-</kbd> rotate one tick</li>
        <li><kbd>Shift</kbd>+<kbd>+</kbd>/<kbd>-</kbd> five ticks</li>
        <li><kbd>M</kbd> mark / <kbd>L</kbd> line, wind side</li>
        <li>same key again cancels</li>
        <li><kbd>Esc</kbd> close</li>
      </ul>
      <p class="fc-shortcuts-hint">Tip: drag the rim to rotate, drag the center card to slide. On phone, drag the grey background on the calculator side to pan.</p>
    </div>
  }
</div>
